import { getSessionStore } from "app/utils/sessionStore";
import { getObjectFromLocalStore } from "app/utils/localStore";
import { USERTYPE } from "app/utils/types";
import { getThemeBasedIAPPBranding, getThemeColors, getLightColor } from "app/utils/utils";
import { Tooltip } from "primereact/tooltip";
import { Link, useLocation, useNavigate } from "react-router-dom";
import separatorImg from "../../assets/svg/separator.svg";
import SummaryIcon from "app/assets/IconComponent/summaryIcon";
import SolutionIcon from "app/assets/IconComponent/solutionIcon";
import FundingIcon from "app/assets/IconComponent/FundingIcon";
import SignatureIcon from "app/assets/IconComponent/SignatureIcon";
import DisclosureIcon from "app/assets/IconComponent/DisclosureIcon";
import TrueIqIcon from "app/assets/IconComponent/TrueIqIcon";
import RectangleCurved from "app/assets/IconComponent/Rectanglecurved";
import React, { useContext } from "react";
import { ThemeContext } from "app/uicomponents/macrocomponents/RootLayout";
import { useSelector } from "react-redux";
import { getIsAgencyManagerActiveData } from "app/redux/features/isAgencyActive";
import PerformanceIcon from "app/assets/IconComponent/Performance";
import AppendixRev from "app/assets/IconComponent/AppendixRev";
import ThreadsRev from "app/assets/IconComponent/ThreadsRev";
import ExitIconIntRev from "app/assets/IconComponent/ExitIconIntRev";

const BASE = "/interactive-review-center";

const InteractiveReviewCenterSidebar = ({
  isCollapsed,
  setIsCollapsed,
}: {
  isCollapsed: boolean;
  setIsCollapsed: (v: boolean) => void;
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDarkMode } = useContext(ThemeContext);
  const { branding } = getSessionStore("lookUpResponse");
  const { primaryColor, secondaryColor } = getThemeColors(branding);
  const { isAgencyManagerActive } = useSelector(getIsAgencyManagerActiveData);
  const userAccess = getObjectFromLocalStore("useraccess");

  const hideTrueIq = userAccess?.userType === USERTYPE.CONTACT;

  const iconColor = isDarkMode ? "#ffffff" : "#66788F";
  const activeColor = isDarkMode ? secondaryColor : primaryColor;

  const isActive = (path: string) => location.pathname.includes(`${BASE}/${path}`);

  const menuItems = [
    {
      label: "Summary",
      path: "summary",
      icon: <SummaryIcon fill={iconColor} />,
    },
    ...(hideTrueIq
      ? []
      : [
          {
            label: "TrueIQ",
            path: "trueIq",
            icon: <TrueIqIcon width={24} height={24} fill={iconColor} />,
          },
        ]),
    {
      label: "Solution",
      path: "solution",
      icon: <SolutionIcon fill={iconColor} />,
    },
    {
      label: "Funding",
      path: "funding",
      icon: <FundingIcon fill={iconColor} />,
    },
    {
      label: "Performance",
      path: "performance",
      icon: <PerformanceIcon fill={iconColor} />,
    },
    {
      label: "Signature",
      path: "signature",
      icon: <SignatureIcon fill={iconColor} />,
    },
  ];

  const bottomMenuItems = [
    {
      label: "Threads",
      path: "thread",
      icon: <ThreadsRev fill={iconColor} />,
    },
    {
      label: "Disclaimer",
      path: "disclaimer",
      icon: <DisclosureIcon fill={iconColor} />,
    },
    {
      label: "Appendix",
      path: "appendix",
      icon: <AppendixRev fill={iconColor} />,
    },
    {
      label: "Exit",
      path: "exit",
      icon: <ExitIconIntRev width={22} fill={iconColor} />,
    },
  ];

  const handleExit = () => {
    // const previousPath = getSessionStore("previousPath");
    // removeSessionStore("interactiveProposalID");
    // removeSessionStore("previousPath");
    navigate("/plan-summary/plan?tab=plan");
  };

  const renderItem = (item: (typeof menuItems)[0], index: number) => {
    const active = isActive(item.path);
    const tipId = `irc_tip_${item.path}`;

    return (
      <li
        key={index}
        className={`my-3 relative group w-full flex justify-center items-center ${
          isCollapsed ? "px-2" : "px-3"
        }`}
        style={{ color: active ? activeColor : "#66788F" }}
        onClick={() => {
          if (item.path === "exit") handleExit();
        }}
      >
        {active && (
          <div className="absolute left-0">
            <RectangleCurved fill={activeColor} />
          </div>
        )}

        {isCollapsed && (
          <Tooltip target={`#${tipId}`} position="right">
            {item.label}
          </Tooltip>
        )}

        <Link
          to={item.path === "exit" ? "#" : `${BASE}/${item.path}`}
          className="flex flex-col items-center"
          onClick={(e) => {
            if (item.path === "exit") e.preventDefault();
          }}
        >
          <div
            id={tipId}
            style={{
              filter: active ? `drop-shadow(0px 3px 6px ${getLightColor(activeColor)})` : "",
            }}
            className="w-[24px] transition-transform duration-200 relative hover:scale-110"
          >
            {React.cloneElement(item.icon as React.ReactElement<any>, {
              fill: active ? activeColor : iconColor,
            })}
          </div>

          {!isCollapsed && (
            <div className={`text-xs mt-1 ${active ? "text-sm font-semibold" : "font-medium"}`}>
              {item.label}
            </div>
          )}
        </Link>
      </li>
    );
  };

  return (
    <div className={`px-1 mb-4 relative z-[100] ${isCollapsed ? "w-[4.25rem]" : "w-[8.5rem]"}`}>
      <aside
        className="fixed z-100 ml-2"
        style={{
          width: isCollapsed ? "4.25rem" : "8.5rem",
          height: "calc(100vh - 30px)",
          marginTop: "15px",
        }}
      >
        <nav
          className="scrollbar-hide h-full flex flex-col justify-content-between overflow-y-auto border-r border-primary-bg-border border-[1px] shadow-lg rounded-2xl pt-3 pb-2 bg-light-primary dark:bg-[#2A2A3D]"
          style={{ scrollbarWidth: "thin" }}
        >
          <div className="flex flex-col h-full">
            {/* Collapse / Expand */}
            <div className="absolute group right-[-20px] top-90 items-center">
              <button type="button" onClick={() => setIsCollapsed(!isCollapsed)}>
                {isCollapsed ? (
                  <i
                    className="irc-right-angles pi pi-angle-double-right p-2 rounded-lg bg-light-primary border-[1px] border-gray-300 dark:border-[1px] dark:bg-blue-1000 hover:bg-gray-100 dark:hover:!bg-blue-800"
                    data-pr-tooltip="Expand"
                  >
                    <Tooltip target=".irc-right-angles" className="tooltip-wrapper" />
                  </i>
                ) : (
                  <i
                    className="irc-left-angles pi pi-angle-double-left p-2 rounded-lg bg-light-primary border-[1px] border-gray-300 dark:bg-blue-1000 hover:bg-gray-100 dark:hover:!bg-blue-800"
                    data-pr-tooltip="Collapse"
                  >
                    <Tooltip target=".irc-left-angles" className="tooltip-wrapper" />
                  </i>
                )}
              </button>
            </div>

            {/* Logo */}
            <div className="flex-none">
              <img
                src={getThemeBasedIAPPBranding(
                  branding,
                  isAgencyManagerActive,
                  isDarkMode,
                  isCollapsed,
                )}
                alt="logo"
                className="mb-10 px-1"
              />
            </div>

            {/* Main menu */}
            <div className="flex-1 overflow-y-auto">
              <div className="flex justify-center items-center">
                <ul
                  className={`leftPanelUl flex-1 text-center conthrax-font ${
                    !isCollapsed ? "w-[48px]" : ""
                  }`}
                >
                  {menuItems.map((item, index) => renderItem(item, index))}
                </ul>
              </div>
            </div>

            {/* Bottom menu */}
            <div className="flex-none">
              <img src={separatorImg} alt="separator" className="my-3" />
              <div className="flex justify-center items-center">
                <ul
                  className={`leftPanelUl flex-1 text-center conthrax-font ${
                    !isCollapsed ? "w-[48px]" : ""
                  }`}
                >
                  {bottomMenuItems.map((item, index) => renderItem(item, index))}
                </ul>
              </div>
            </div>
          </div>
        </nav>
      </aside>
    </div>
  );
};

export default InteractiveReviewCenterSidebar;
